import { computed } from '@angular/core';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { withSyncToWebStorage } from './with-storage.feature';

export interface HiddenIdsState<TId> {
  /**
   * What the user has switched **off**.
   *
   * Hidden ids are stored rather than shown ones so that something added later — a widget, a
   * location the administrator is given access to, a column a release adds — appears instead of
   * staying silently switched off, and so that "no preference at all" means "everything".
   */
  hiddenIds: TId[];
}

/**
 * A remembered "which of these are switched off" preference.
 *
 * One mechanism for the dashboard's three preferences — widgets, locations and columns — which were
 * three copies of the same store before. Each keeps its own state and its own storage key; only
 * the machinery is shared, so moving any of them to the user profile on the API later is one
 * change rather than three.
 *
 * @param options.key Web-storage key; must be unique per preference
 * @param options.isKnownId Optional guard dropping stored ids that no longer exist, so a
 * preference saved against an older catalogue cannot hide something that now carries that id
 * @param options.beforeRestore Transforms the raw parsed JSON before it reaches `patchState`, for
 * a store that has to migrate what an older release wrote under a key this state no longer
 * declares — `patchState` silently drops (with a console warning) any key that is not part of
 * the initial state, so a migration has to rename it before that first patch, not after. A
 * function rather than a rule of its own: a one-off migration belongs next to the store it is
 * about, not in the mechanism every store shares.
 * @param options.initialHiddenIds What is switched off for someone who has never chosen. Stored
 * state replaces it wholesale, so "nothing hidden" stays a choice a user can make and keep — it
 * is only the starting point that differs.
 * @returns A store feature exposing `hidden` (a Set), `isVisible`, `toggle`, `showAll` and
 * `hideAll`
 */
export function withHiddenIds<TId extends string | number>({
  key,
  isKnownId,
  beforeRestore,
  initialHiddenIds = [],
}: {
  key: string;
  isKnownId?: (id: TId) => boolean;
  beforeRestore?: (state: Record<string, unknown>) => Record<string, unknown> | void;
  initialHiddenIds?: readonly TId[];
}) {
  return signalStoreFeature(
    withState<HiddenIdsState<TId>>({ hiddenIds: [...initialHiddenIds] }),
    withSyncToWebStorage({ key, type: 'local', beforeRestore }),
    withComputed(({ hiddenIds }) => ({
      hidden: computed(() => new Set(isKnownId ? hiddenIds().filter(isKnownId) : hiddenIds())),
    })),
    // Read off the raw state rather than the `hidden` computed: annotating the store to reach a
    // computed loses the state source `patchState` needs. An id the guard would have dropped can
    // never equal one that exists, so the two answer alike.
    withMethods((store) => ({
      isVisible(id: TId): boolean {
        return !store.hiddenIds().includes(id);
      },

      toggle(id: TId): void {
        const hidden = new Set(store.hiddenIds());

        if (!hidden.delete(id)) {
          hidden.add(id);
        }

        patchState(store, { hiddenIds: [...hidden] });
      },

      showAll(): void {
        patchState(store, { hiddenIds: [] });
      },

      /**
       * Switches every one of `ids` off
       * @param ids The full catalogue — the store holds what is hidden, not what exists, so it
       * has to come from whoever asked
       */
      hideAll(ids: readonly TId[]): void {
        patchState(store, { hiddenIds: [...ids] });
      },
    })),
  );
}
